import { DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { ItemNote } from 'src/app/_models/itemNote';
import { Member } from 'src/app/_models/member';
import { PlanItem } from 'src/app/_models/planItem';
import { AlertifyService } from 'src/app/_services/alertify.service';
import { AuthService } from 'src/app/_services/auth.service';
import { ItemNoteService } from 'src/app/_services/itemNote.service';
import { PlanService } from 'src/app/_services/plan.service';

interface NumericOption {
  value: number | null;
  display: string;
}

interface StringOption {
  value: string;
  display: string;
}

@Component({
  selector: 'app-itemNotes',
  templateUrl: './itemNotes.component.html',
  styleUrls: ['./itemNotes.component.css']
})
export class ItemNotesComponent implements OnInit {
  @BlockUI() blockUI: NgBlockUI;
  member: Member;
  planItem: PlanItem;
  form: FormGroup = new FormGroup({ });
  editItemNoteIndex: number = -1;
  
  allowedRecipients: NumericOption[] = [ ];
  noteTypes: StringOption[] = [ { value: "TEXT", display: "Text" }, { value: "MP4", display: "MP4" } , { value: "TUBE", display: "YouTube" }] ;

  uploadedMP4: File[] = [];

  constructor(private route: ActivatedRoute
      , private router: Router
      , private alertify: AlertifyService
      , private planService: PlanService
      , private itemNoteService: ItemNoteService
      , private authService: AuthService
      , private formBuilder: FormBuilder
      , public datePipe: DatePipe) { }

  ngOnInit() {
    this.member = this.authService.currentMember;
    this.route.params.subscribe((params) => {
      if (params) {
        const planItemId = params.id;
        this.planService.getPlanItem(planItemId).subscribe(planItem => {
          if (planItem == null) {
            this.alertify.error("Cannot retrieve Plan Item to display the notes");
          }

          this.planItem = planItem;
          this.form = this.formBuilder.group(
            { id: [ this.planItem.id ]
              , sequence: [ this.planItem.sequence ]
              , type: [ this.planItem.type ]
              , isActive: [ this.planItem.isActive ]
              , value: [ this.planItem.value ]
              , lessonPlan: [ this.planItem.lessonPlan ]
              , itemNotes: this.formBuilder.array([])
            }
          );
          this.loadItemNotes();
          this.loadNoteRecipients();

        }, error => {
          this.alertify.error(error);
        });
      } 
     });
  }

  loadNoteRecipients() {
    if (this.member.id === this.planItem.lessonPlan.member.id) {
      this.allowedRecipients.push({ value: 0, display: "Public" });
      this.allowedRecipients.push({ value: this.member.id, display: "Private" });
      this.planItem.lessonPlan.planMembers.filter(pm => pm.accepted === true).forEach(planMember => {
        this.allowedRecipients.push( { value: planMember.member.id, display: planMember.member.knownAs});
      });
    } else if (this.planItem.lessonPlan.planMembers.find(pm => pm.member != null && pm.member.id === this.member.id && pm.accepted && pm.canUpdate)) {
      this.allowedRecipients.push({ value: 0, display: "Public" });
      this.allowedRecipients.push({ value: this.planItem.lessonPlan.member.id, display: this.planItem.lessonPlan.member.knownAs});
    } else {
      this.allowedRecipients.push({ value: this.planItem.lessonPlan.member.id, display: this.planItem.lessonPlan.member.knownAs});
    }
  }

  loadItemNotes() {
    const control = <FormArray>this.form.get('itemNotes');
    this.planItem.itemNotes.forEach(note => {
      const itemNote = this.formBuilder.group(
        {
          id: [ note.id ],
          planItemId: [ note.planItemId ],
          noteType: [ note.noteType],
          memberId: [ note.memberId ],
          member: [ note.member ],
          toId: [ note.toId ? note.toId : 0],
          to: [ note.to ],
          value: [ note.value ]
        }
      );
      control.push(itemNote)
    });
  }

  itemNote(index: number) {
    var itemNote = this.itemNotes.at(index) as FormGroup;
    return itemNote;
  }

  get itemNotes() {
    return this.form.controls["itemNotes"] as FormArray;
  }

  setEditItemNote(index: number) {
    const editNote = this.itemNotes.at(index) as FormGroup;
    if (editNote.controls.memberId.value === this.member.id) {
      this.editItemNoteIndex = index;
    }    
  }

  addItemNote() {
    var toId = (this.allowedRecipients.length === 1) ? this.allowedRecipients[0].value: null;
    const itemNote = this.formBuilder.group(
      {
        id: [ 0 ],
        planItemId: this.planItem.id,
        noteType: [ 'TEXT', Validators.required ],
        memberId: [ this.member.id ],
        member: [ this.member ],
        toId: [ toId ],
        to: [ null ],
        value: [ '' ]
      }
    );
    this.itemNotes.push(itemNote);
    this.editItemNoteIndex = this.itemNotes.length -1;
  }

  deleteItemNote(index: number) {
    this.itemNotes.removeAt(index);
    this.editItemNoteIndex = -1;
  }

  onFileChange(index: number, event: any) {
    const file = event.target.files[0];
    if(file.size > 100000000) {
      alert("Video cannot exceed 100MB");
      return;
   }
    var itemNote = this.itemNote(index);
    itemNote.patchValue({
      value: file.name
    });
    this.uploadedMP4.push(file);
  }


  onSubmit() {
    if (this.form.invalid) {
      this.alertify.error("Form is invalid");
      return;
    }
    this.blockUI.start("Submitting changes - please wait");

    this.itemNoteService.updateItemNote(this.planItem.id, this.itemNotes.value, this.uploadedMP4).subscribe(itemNotes => {
      this.blockUI.stop();
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/lessonPlan/planItem/' + this.planItem.id])});
    }, error => {
      this.alertify.error(error);
      this.blockUI.stop();
    });
  }
}
